import { NetworkPasswordComplexity } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useNetwork, useUpdateNetwork } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

export const PasswordStrength = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { mutateAsync: updateNetwork, isLoading } = useUpdateNetwork()

  return (
    <Card>
      <Card.Header
        title={$t({
          defaultMessage: 'Password strength',
          id: 'Admin.Security.PasswordStrength',
        })}
        description={$t({
          defaultMessage:
            'As an admin, you can enforce password requirements to protect your users’ accounts and meet your organization’s compliance needs. You can require a certain number of characters and symbols for passwords.',
          id: 'Admin.Security.PasswordStrength.Description',
        })}
      />
      <Card.Content>
        <Form
          defaultValues={{ passwordComplexity: network?.passwordComplexity }}
          onSubmit={data => {
            updateNetwork({ input: data })
              .then(() => {
                toast({
                  title: $t({
                    defaultMessage: 'Security settings updated.',
                    id: 'Admin.Security.SettingsUpdated',
                  }),
                  status: 'success',
                })
              })
              .catch(() => {
                toast({
                  title: $t({
                    defaultMessage: 'Unable to update security settings.',
                    id: 'Admin.Security.FailedToUpdateSettings',
                  }),
                  status: 'error',
                })
              })
          }}
        >
          {({ formState }) => {
            return (
              <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
                <Form.RadioGroup name="passwordComplexity">
                  <RadioGroup.Items>
                    <RadioGroup.Item
                      value={NetworkPasswordComplexity.Simple}
                      title={$t({
                        defaultMessage: 'Average',
                        id: 'Admin.Security.PasswordComplexity.Average',
                      })}
                      description={$t({
                        defaultMessage:
                          'Password should have 1 lowercase letter, 1 uppercase letter, 1 number, and be at least 8 characters long',
                        id: 'Admin.Security.PasswordComplexity.Average.Description',
                      })}
                    />
                    <RadioGroup.Item
                      value={NetworkPasswordComplexity.Standard}
                      title={$t({
                        defaultMessage: 'Standard',
                        id: 'Admin.Security.PasswordComplexity.Standard',
                      })}
                      description={$t(
                        {
                          defaultMessage:
                            'Password should have 1 lowercase letter, 1 uppercase letter, <bold>1 special character</bold>, and be at least 8 characters long',
                          id: 'Admin.Security.PasswordComplexity.Standard.Description',
                        },
                        {
                          bold: chunks => (
                            <span className="font-semibold">{chunks}</span>
                          ),
                        },
                      )}
                    />
                    <RadioGroup.Item
                      value={NetworkPasswordComplexity.Complex}
                      title={$t({
                        defaultMessage: 'Strong',
                        id: 'Admin.Security.PasswordComplexity.Strong',
                      })}
                      description={$t(
                        {
                          defaultMessage:
                            'Password should have 1 lowercase letter, 1 uppercase letter, 1 number, <bold>1 special character</bold>, and be at least <bold>10 characters long</bold>',
                          id: 'Admin.Security.PasswordComplexity.Strong.Description',
                        },
                        {
                          bold: chunks => (
                            <span className="font-semibold">{chunks}</span>
                          ),
                        },
                      )}
                    />
                  </RadioGroup.Items>
                </Form.RadioGroup>
                <p className="text-sm text-content-subdued font-medium">
                  <T
                    defaultMessage="These policies don’t apply in some cases, such as when users are authenticated by a third party identity provider."
                    id="Admin.Security.PasswordComplexity.PolicyException"
                  />
                </p>
                <Form.Actions>
                  <Button
                    type="submit"
                    loading={isLoading}
                    disabled={!formState.isDirty}
                  >
                    <T defaultMessage="Update" id="Generics.Update" />
                  </Button>
                </Form.Actions>
              </div>
            )
          }}
        </Form>
      </Card.Content>
    </Card>
  )
}
